/**
 * LeftShield Portal - AI Decision Detail Page
 * Full traceability view of a single AI decision.
 * Route: /ai-governance/decisions/:id | Category: AI
 *
 * §18 GUID-routing fix (#12737): reads the real GUID `:id` route param and fetches the decision
 * from `GET /api/portal/ai-governance/decisions/{id:guid}` (`AiGovernanceController.GetDecision`)
 * instead of rendering a hardcoded `DEMO_DECISION`. Only fields present on the real `AiDecisionDto`
 * (`IAiGovernanceService.cs`) are rendered — see `src/services/api.ts` → `AiDecision`. Sections that
 * depended on mock-only data (per-rule constitution breakdown, validator agreement score, a
 * fabricated raw-reasoning payload) have no real-API equivalent yet and have been replaced with the
 * real fields that ARE available (`outputSummary`, the four pipeline status fields, `evidenceRefs`).
 */
import React from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Card,
  Badge,
  Button,
  Spinner,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  BrainCircuitRegular,
  ShieldCheckmarkRegular,
  DocumentCheckmarkRegular,
  CheckmarkCircleRegular,
  GavelRegular,
  ErrorCircleRegular,
} from '@fluentui/react-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { governanceTokens } from '../components/design-system';
import { aiGovernanceApi } from '../services/api';

// ---------------------------------------------------------------------------
// Local styles
// ---------------------------------------------------------------------------

const AI_COLOR = '#8852E0';

const useLocalStyles = makeStyles({
  traceFlow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalL,
    borderLeft: `2px solid ${AI_COLOR}`,
  },
  traceStep: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  traceIcon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  metaPair: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  centeredState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
  },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Generic status-badge colour mapper for the free-text pipeline status fields on `AiDecisionDto`
 *  (`constitutionStatus`, `validationStatus`, `evidenceSupportStatus`, `finalStatus`). The API does
 *  not constrain these to an enum, so this maps common values defensively rather than assuming an
 *  exhaustive set. */
const statusBadgeColor = (status: string): 'success' | 'danger' | 'warning' | 'informative' => {
  const s = status.toLowerCase();
  if (['pass', 'passed', 'approved', 'accepted', 'verified', 'complete', 'completed'].includes(s)) return 'success';
  if (['fail', 'failed', 'rejected', 'unsupported', 'violation', 'violated'].includes(s)) return 'danger';
  if (['pending', 'review_required', 'review-required', 'warning', 'in_review'].includes(s)) return 'warning';
  return 'informative';
};

const riskBadgeColor = (level: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (level.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

const refTypeBadgeColor = (refType: string): 'danger' | 'warning' | 'brand' | 'informative' | 'success' => {
  switch (refType.toLowerCase()) {
    case 'cve': return 'danger';
    case 'policy': return 'brand';
    case 'control': return 'informative';
    case 'scan': return 'warning';
    case 'history': return 'success';
    default: return 'informative';
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AIDecisionDetailPage: React.FC = () => {
  const ps = usePageStyles();
  const ls = useLocalStyles();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: decision,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['ai-governance-decision', id],
    queryFn: () => aiGovernanceApi.getDecision(id!),
    enabled: !!id,
  });

  if (!id) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="AI Decision Detail"
          subtitle="No decision ID was provided."
          icon={<BrainCircuitRegular />}
          iconColor={AI_COLOR}
          actions={
            <Button appearance="subtle" onClick={() => navigate('/ai-governance')}>
              Back to AI Governance
            </Button>
          }
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="AI Decision Detail"
          subtitle={`Loading traceability record for decision ${id}…`}
          icon={<BrainCircuitRegular />}
          iconColor={AI_COLOR}
        />
        <Card>
          <div className={ls.centeredState}>
            <Spinner label="Loading decision…" />
          </div>
        </Card>
      </div>
    );
  }

  if (isError || !decision) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="AI Decision Detail"
          subtitle={`Decision ${id}`}
          icon={<BrainCircuitRegular />}
          iconColor={AI_COLOR}
          actions={
            <Button appearance="subtle" onClick={() => navigate('/ai-governance')}>
              Back to AI Governance
            </Button>
          }
        />
        <Card>
          <div className={ls.centeredState}>
            <ErrorCircleRegular style={{ fontSize: '32px', color: governanceTokens.colorStatusError }} />
            <Body1>Unable to load this decision. It may not exist, or you may not have access to it.</Body1>
            <Button appearance="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const d = decision;

  return (
    <div className={ps.page}>
      <PageHeader
        title="AI Decision Detail"
        subtitle={`Traceability record for decision ${d.id}`}
        icon={<BrainCircuitRegular />}
        iconColor={AI_COLOR}
        actions={
          <Button appearance="subtle" onClick={() => navigate('/ai-governance')}>
            Back to AI Governance
          </Button>
        }
      />

      {/* Decision Summary */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>Decision Summary</Title2>
            <Badge appearance="tint" color="brand">{d.decisionType}</Badge>
          </div>

          <div className={ps.metricGrid}>
            <div className={ls.metaPair}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Agent</Caption1>
              <Body1 style={{ fontWeight: 600 }}>{d.agentName} <Caption1>{d.agentVersion}</Caption1></Body1>
            </div>
            <div className={ls.metaPair}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Subject</Caption1>
              <Body1 style={{ fontWeight: 600 }}>{d.subjectId}</Body1>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{d.subjectType}</Caption1>
            </div>
            <div className={ls.metaPair}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Timestamp</Caption1>
              <Body1>{new Date(d.createdAt).toLocaleString()}</Body1>
            </div>
            <div className={ls.metaPair}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Decision ID</Caption1>
              <Body1 className={ps.metricValue}>{d.id}</Body1>
            </div>
          </div>
        </div>
      </Card>

      <div className={ps.twoColumn}>
        {/* Confidence & Risk */}
        <Card>
          <div className={ps.cardBody}>
            <Title2>Confidence & Risk</Title2>

            <div className={ps.statGrid}>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Confidence Score</Body2>
                <Title2 className={ps.metricValue} style={{ color: AI_COLOR }}>
                  {d.confidenceScore.toFixed(2)}
                </Title2>
                <div style={{ width: '100%', height: '6px', backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusSmall }}>
                  <div style={{ width: `${d.confidenceScore * 100}%`, height: '6px', backgroundColor: AI_COLOR, borderRadius: tokens.borderRadiusSmall }} />
                </div>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Risk Level</Body2>
                <Badge appearance="tint" color={riskBadgeColor(d.riskLevel)} size="large">
                  {d.riskLevel.toUpperCase()}
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Final Status */}
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.sectionHeader}>
              <Title2>Final Status</Title2>
              <Badge
                appearance="tint"
                color={statusBadgeColor(d.finalStatus)}
                icon={<CheckmarkCircleRegular />}
              >
                {d.finalStatus.toUpperCase()}
              </Badge>
            </div>
            <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
              Computed from the constitution, validation, and evidence-support pipeline below.
            </Body2>
          </div>
        </Card>
      </div>

      {/* Governance Pipeline Status */}
      <Card>
        <div className={ps.cardBody}>
          <Title2>Governance Pipeline Status</Title2>

          <div className={ps.statGrid}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Constitution Status</Body2>
              <Badge appearance="tint" color={statusBadgeColor(d.constitutionStatus)}>
                {d.constitutionStatus}
              </Badge>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Validation Status</Body2>
              <Badge appearance="tint" color={statusBadgeColor(d.validationStatus)}>
                {d.validationStatus}
              </Badge>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Evidence Support Status</Body2>
              <Badge appearance="tint" color={statusBadgeColor(d.evidenceSupportStatus)}>
                {d.evidenceSupportStatus}
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* AI Output Summary — real `outputSummary` field (replaces a fabricated raw-reasoning payload) */}
      <Card>
        <div className={ps.cardBody}>
          <Title2>AI Output Summary</Title2>
          <Body1>{d.outputSummary || 'No output summary recorded for this decision.'}</Body1>
        </div>
      </Card>

      {/* Supporting Evidence References */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>Supporting Evidence References</Title2>
            <Badge appearance="outline" color="informative">
              {d.evidenceRefs.length} references
            </Badge>
          </div>

          {d.evidenceRefs.length === 0 ? (
            <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
              No evidence references are attached to this decision.
            </Body2>
          ) : (
            <div className={ps.rowList}>
              {d.evidenceRefs.map((ev, idx) => (
                // eslint-disable-next-line react/no-array-index-key -- refs have no stable id in the DTO
                <div key={`${ev.refType}-${ev.refId}-${idx}`} className={ps.rowItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <Badge appearance="tint" color={refTypeBadgeColor(ev.refType)} size="small">
                      {ev.refType}
                    </Badge>
                    <Body1 style={{ fontWeight: 600 }}>{ev.refId}</Body1>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Traceability Visual — driven by the real pipeline status fields, not fabricated narrative */}
      <Card>
        <div className={ps.cardBody}>
          <Title2>Traceability Flow</Title2>

          <div className={ls.traceFlow}>
            <div className={ls.traceStep}>
              <span className={ls.traceIcon} style={{ color: AI_COLOR }}><BrainCircuitRegular /></span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
                <Body1 style={{ fontWeight: 600 }}>AI Decision</Body1>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  {d.agentName} produced a {d.decisionType.toLowerCase()} with confidence {d.confidenceScore.toFixed(2)}
                </Caption1>
              </div>
            </div>

            <div className={ls.traceStep}>
              <span className={ls.traceIcon} style={{ color: '#13B5B1' }}><GavelRegular /></span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
                <Body1 style={{ fontWeight: 600 }}>Constitution Check</Body1>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  Status: {d.constitutionStatus}
                </Caption1>
              </div>
            </div>

            <div className={ls.traceStep}>
              <span className={ls.traceIcon} style={{ color: '#E89652' }}><DocumentCheckmarkRegular /></span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
                <Body1 style={{ fontWeight: 600 }}>Evidence Support</Body1>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  {d.evidenceRefs.length} reference(s) linked — status: {d.evidenceSupportStatus}
                </Caption1>
              </div>
            </div>

            <div className={ls.traceStep}>
              <span className={ls.traceIcon} style={{ color: '#1FD2D3' }}><ShieldCheckmarkRegular /></span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
                <Body1 style={{ fontWeight: 600 }}>Validation</Body1>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  Status: {d.validationStatus}
                </Caption1>
              </div>
            </div>

            <div className={ls.traceStep}>
              <span className={ls.traceIcon} style={{ color: governanceTokens.colorStatusSuccess }}><CheckmarkCircleRegular /></span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
                <Body1 style={{ fontWeight: 600 }}>Outcome</Body1>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  Final status: {d.finalStatus}
                </Caption1>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
